import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { useEffect } from "react";
import {
  markAnalyticsReady,
  optInAnalytics,
  optOutAnalytics,
  registerDeploymentProperties,
} from "@/lib/analytics";
import { isImpersonating } from "@/lib/impersonation";

const isDev = import.meta.env.DEV;

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (isDev) return;

    const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
    const posthogHost = import.meta.env.VITE_POSTHOG_HOST;
    const enableSessionReplay =
      import.meta.env.VITE_POSTHOG_SESSION_REPLAY === "true";

    // Route events through a same-origin path so ad-blockers (uBlock, Brave,
    // AdGuard, Pi-hole, corp DNS filters) can't pattern-match the PostHog
    // hostname — typical recovery is +20–40% of events. The deployment's
    // reverse proxy MUST rewrite `/ingest/*` → posthog cloud:
    //   /ingest/static/* → https://us-assets.i.posthog.com/static/*
    //   /ingest/*        → https://us.i.posthog.com/*
    // `ui_host` keeps "Open in PostHog" links pointing at the real PostHog UI.
    // If VITE_POSTHOG_PROXY_PATH is not set, we fall back to the direct host
    // so existing dev/staging envs keep working unchanged.
    const proxyPath = import.meta.env.VITE_POSTHOG_PROXY_PATH;
    const apiHost = proxyPath || posthogHost;

    // Don't initialize PostHog if the key is a placeholder or missing
    if (
      posthogKey &&
      posthogHost &&
      posthogKey !== "your_posthog_project_api_key" &&
      !posthogKey.includes("your_")
    ) {
      posthog.init(posthogKey, {
        api_host: apiHost,
        ui_host: proxyPath ? posthogHost : undefined,
        person_profiles: "identified_only",
        // Each company/tenant is served on its own subdomain
        // (`company-a.stockline.ai`, `company-b.stockline.ai`). PostHog defaults
        // to a cross-subdomain identity cookie (scoped to `.stockline.ai`
        // because the registrable domain is not a public suffix), which would
        // share `distinct_id` across every tenant and trigger a permanent
        // cross-tenant person merge on the next `$identify`. Pin the cookie to
        // the exact origin so each subdomain has a fully isolated identity store.
        //
        // `cross_subdomain_cookie` only controls where NEW cookies are written;
        // a browser that already has the legacy `.stockline.ai`-scoped cookie
        // (default name `ph_<token>_posthog`) would still have it read on
        // startup, re-importing the contaminated `distinct_id` until it expires
        // (up to 365 days). Scoping `persistence_name` to the hostname changes
        // the storage key per subdomain, so the legacy shared cookie is never
        // read and each origin starts from an isolated identity.
        persistence_name: `posthog_${window.location.hostname}`,
        cross_subdomain_cookie: false,
        capture_pageview: false, // We'll use PostHogPageView for better control
        capture_pageleave: true,
        autocapture: true,
        // Capture uncaught JS errors + unhandled promise rejections as
        // `$exception` events (PostHog Error Tracking). This is the SDK-side
        // switch — it does NOT need the project-level "exception autocapture"
        // toggle, and setting it here is the reliable, per-build way to turn it
        // on. Console errors are left OFF (`capture_console_errors` default
        // false) so a noisy `console.error` doesn't inflate the issue list.
        // The SDK installs its own window.onerror/onunhandledrejection handlers;
        // do NOT add manual handlers elsewhere or every error double-counts.
        capture_exceptions: {
          capture_unhandled_errors: true,
          capture_unhandled_rejections: true,
          capture_console_errors: false,
        },
        // Emit Core Web Vitals (`$web_vitals`: LCP, CLS, INP, FCP) so we can see
        // the client's REAL field UX, not just synthetic Lighthouse. `web_vitals`
        // must be set explicitly — it is gated behind remote config otherwise, so
        // a fresh project would stay silent. Network-timing capture keeps its
        // default; we only assert web vitals here.
        capture_performance: { web_vitals: true },
        // Enable session replay if configured
        disable_session_recording: !enableSessionReplay,
        session_recording: enableSessionReplay
          ? {
              recordCrossOriginIframes: true,
              // Input values stay VISIBLE by product choice (the team reviews
              // what users type in the recap grid), EXCEPT password fields,
              // which must never reach a replay — the login form is part of the
              // app, so an unmasked recording would capture typed credentials.
              // Mask any other sensitive field at the callsite with the
              // `data-posthog-mask` attribute or the `.ph-no-capture` class.
              maskAllInputs: false,
              maskInputOptions: { password: true },
              maskTextSelector: "[data-posthog-mask]", // Mask only elements with this attribute
            }
          : undefined,
        // `loaded` is the documented stable hook for "init() has finished".
        // Flip the analytics-module readiness flag here and register the
        // deployment-stable super properties (environment, app_version) via
        // the shared helper — the same helper is called from
        // `resetAnalytics()` so those props survive a user-switch on shared
        // devices (PostHog's `reset()` wipes the persistence store).
        loaded: () => {
          markAnalyticsReady();
          registerDeploymentProperties();
          // Deterministically set the capture opt-in/out state on EVERY boot
          // from the impersonation marker, rather than trusting whatever the
          // persisted `__opt_in_out` store held. Impersonation navigates with a
          // full reload (location.href = "/home"), so the opt-out set at
          // impersonation start must be re-applied here to survive the reload.
          // Conversely a normal session after exiting impersonation (a full
          // logout that clears the marker) must be opted back IN, since PostHog
          // persists opt-out and would otherwise stay silent.
          if (isImpersonating()) {
            optOutAnalytics();
          } else {
            optInAnalytics();
          }
        },
      });
    }
  }, []);

  // Skip PHProvider entirely in dev to prevent any network requests
  if (isDev) return <>{children}</>;

  return <PHProvider client={posthog}>{children}</PHProvider>;
}
